import { Ingredient } from '../types';
import { scaleIngredientMacro } from './mealCalculations';
import { normalizeUnit, convertAmount, getBaseMeasurementIndex } from './unitConversion';

/**
 * Compute updated ingredients from current ingredients and form values (amounts/units).
 * Form values are multiplied by multiplier for display; we divide to get per-portion values.
 */
export function calculateUpdatedIngredients(
  ingredients: Ingredient[],
  values: { [key: string]: number | string },
  multiplier: number
): Ingredient[] {
  return ingredients.map((ingredient) => {
    const formValue = values[ingredient.id];
    const selectedUnit = (values[`${ingredient.id}_unit`] as string) || null;

    if (
      formValue !== undefined &&
      typeof formValue === 'number' &&
      ingredient.measurements &&
      ingredient.measurements.length > 0
    ) {
      const amountPer1Portion = formValue / multiplier;
      const measurementIndex = getBaseMeasurementIndex(ingredient.measurements);
      const currentMeasurement = ingredient.measurements[measurementIndex];
      const currentStoredAmount = currentMeasurement.amount;
      const originalUnit = normalizeUnit(currentMeasurement.unit);
      const unitToStore =
        selectedUnit && selectedUnit !== originalUnit ? selectedUnit : originalUnit;
      let amountForRatioCalculation = amountPer1Portion;
      if (selectedUnit && selectedUnit !== originalUnit) {
        amountForRatioCalculation = convertAmount(amountPer1Portion, selectedUnit, originalUnit);
      }
      const ratio = currentStoredAmount > 0 ? amountForRatioCalculation / currentStoredAmount : 1;

      return {
        ...ingredient,
        kcal: ingredient.kcal * ratio,
        // The edited measurement takes the entered amount; the household measure follows it.
        measurements: ingredient.measurements.map((measurement, index) =>
          index === measurementIndex
            ? { ...measurement, amount: amountPer1Portion, unit: unitToStore }
            : { ...measurement, amount: measurement.amount * ratio }
        ),
        macro: scaleIngredientMacro(ingredient.macro, ratio),
      };
    }
    return ingredient;
  });
}

/**
 * Build Formik initial values for ingredients (amounts and units) from ingredient list and multiplier.
 */
export function buildIngredientFormValues(
  ingredients: Ingredient[],
  multiplier: number,
  mealName: string,
  isBarcode = false
): { [key: string]: number | string | null } {
  const values: { [key: string]: number | string | null } = { mealName };
  for (const ingredient of ingredients) {
    const baseMeasurement =
      ingredient.measurements[getBaseMeasurementIndex(ingredient.measurements)];
    const baseAmount = baseMeasurement?.amount ?? 0;
    const baseUnit = baseMeasurement?.unit ?? '';
    values[ingredient.id] = Math.round(baseAmount * multiplier * 100) / 100;
    values[`${ingredient.id}_unit`] = normalizeUnit(baseUnit);
    if (isBarcode) {
      if (
        (baseAmount === 100 &&
          (baseUnit === 'g' ||
            baseUnit === 'grams' ||
            baseUnit === 'ml' ||
            baseUnit === 'milliliters')) ||
        (baseAmount === 10 && baseUnit === 'cl') ||
        (baseAmount === 3 &&
          (baseUnit === 'oz' || baseUnit === 'fl_oz' || baseUnit === 'fluid ounces'))
      ) {
        values[ingredient.id] = null;
      }
    }
  }
  return values;
}
